import { NavLink } from 'react-router-dom'
import { Logo, Press } from '@dorixona/ui'
import { Icon } from '../ui/Icon'
import { useSession } from '../features/auth/session'
import { initials } from '../features/auth/model'
import type { AdminMe } from '../features/auth/api'
import { activeModules } from './modules'

export interface SidebarProps {
  /** Кто вошёл: карточка внизу панели вместо отдельной шапки. */
  me: AdminMe
  open: boolean
  onNavigate: () => void
  onSignOut: () => void
  /** Сколько новых заказов ждёт — метка у пункта «Заказы». */
  newOrders: number
  /** Сколько уведомлений не прочитано — метка у строки «Уведомления» над профилем. */
  unread?: number
}

/** Боковая панель: разделы, доступные роли, внизу — кто вошёл и выход. */
export function Sidebar({ me, open, onNavigate, onSignOut, newOrders, unread = 0 }: SidebarProps) {
  const { can } = useSession()
  // Уведомления — не пункт списка разделов, а строка над профилем (флаг и право — те же).
  const bell = activeModules().find((m) => m.id === 'notifications' && can(m.id))

  return (
    <aside className="side" data-open={open} aria-label="Разделы">
      <div className="side-logo">
        <Logo onDark />
      </div>
      <nav className="side-nav">
        {activeModules()
          .filter((m) => m.inNav !== false && can(m.id))
          .map((m) => (
            <NavLink key={m.id} to={m.path} end={m.path === '/'} onClick={onNavigate}>
              <Icon name={m.icon} />
              {m.name}
              {m.id === 'orders' && newOrders > 0 ? (
                // Жёлтый — только «очередь ждёт вас»; число подписано для читалки.
                <span className="ui-count" data-attention>
                  {newOrders}
                  <span className="visually-hidden"> новых</span>
                </span>
              ) : null}
            </NavLink>
          ))}
      </nav>
      {bell ? (
        // Уведомления — отдельной строкой над профилем, не среди разделов.
        // Число не жёлтое: жёлтый — только очередь заказов.
        <nav className="side-nav side-nav-foot" aria-label="Уведомления">
          <NavLink to={bell.path} onClick={onNavigate}>
            <Icon name={bell.icon} />
            {bell.name}
            {unread > 0 ? (
              <span className="ui-count">
                {unread > 99 ? '99+' : unread}
                <span className="visually-hidden"> непрочитанных</span>
              </span>
            ) : null}
          </NavLink>
        </nav>
      ) : null}
      <div className="side-me">
        <span className="av" aria-hidden="true">
          {initials(me.name)}
        </span>
        <span className="who">
          <b>{me.name}</b>
          <small>{me.roleName}</small>
        </span>
        <Press
          as="button"
          type="button"
          className="ui-iconbtn side-exit"
          squish="icon"
          aria-label="Выйти из системы"
          data-tip="Выйти из системы"
          onPress={onSignOut}
        >
          <Icon name="i-exit" />
        </Press>
      </div>
    </aside>
  )
}
